import { defineStore } from 'pinia'
import { Ref, computed, isRef, ref } from 'vue'

export type DataSource<Data = unknown> = {
  id: string
  data: Data | undefined
  isLoading: boolean
  error: any
}

export const useDataSources = defineStore('dataSources', () => {
  const dataSources = ref<Record<string, DataSource | undefined>>({})

  const getDataSource = <T>(id: Ref<string | undefined>): Ref<DataSource<T> | undefined> => {
    return computed(() => (id.value ? (dataSources.value[id.value] as DataSource<T>) : undefined))
  }

  const getDataSources = <T>(
    ids: Ref<string[] | undefined>
  ): Ref<Record<string, DataSource<T> | undefined> | undefined> => {
    return computed(() => {
      const map = ids.value?.reduce((acc, id) => {
        acc[id] = dataSources.value[id] as DataSource<T>
        return acc
      }, {} as Record<string, DataSource<T> | undefined>)
      return map
    })
  }

  const setDataSource = (dataSource: DataSource) => {
    dataSources.value = {
      ...dataSources.value,
      [dataSource.id]: dataSource
    }
  }

  const reset = () => {
    dataSources.value = {}
  }

  return {
    dataSources,
    getDataSource,
    setDataSource,
    getDataSources,
    reset
  }
})

export function useDataSource<T>(id: string | undefined): Ref<DataSource<T> | undefined>
export function useDataSource<T>(id: Ref<string | undefined>): Ref<DataSource<T> | undefined>
export function useDataSource<T>(
  id: Ref<string[] | undefined>
): Ref<Record<string, DataSource<T> | undefined>>
export function useDataSource<T>(
  id: Ref<string | undefined> | Ref<string[] | undefined> | string | undefined
): Ref<DataSource<T> | undefined | Record<string, DataSource<T> | undefined>> {
  const idRef = isRef(id) ? id : ref(id)
  const store = useDataSources()

  if (Array.isArray(idRef.value)) {
    return store.getDataSources(idRef as Ref<string[] | undefined>)
  }

  return store.getDataSource(idRef as Ref<string | undefined>)
}
